




































































































import { Component, Watch, Vue } from 'vue-property-decorator';

import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { TPatient } from '@/plugins/podolib/services/entities/EntityPatients';

import PatientAddOrEdit from '@/components/patients/PatientAddOrEdit.vue';
import PatientDelete from '@/components/patients/PatientDelete.vue';
import { Severity } from '@sentry/types';

type TSyncableTable = {
  headers: object[];
  patients: {
    list: TEntity<TPatient>[];
    total: number;
  };
  options: {
    page: number;
    itemsPerPage: number;
    sortBy: string[];
    sortDesc: boolean[];
  };
  search: {
    // The path of the fields you want to include in the search
    fields: string[];
    // The value of the search
    value: string;
  };
  // Delay between API call
  delay: {
    config: number;
    timeout: any;
  };
  pending: boolean;
  error: Error | null;
};

interface IPatientsList extends Vue {
  table: TSyncableTable;
  search(): Promise<void>;
}

@Component<IPatientsList>({
  components: { PatientDelete, PatientAddOrEdit },
  created() {
    // Set header with translations
    this.table.headers = [
      {
        text: this.$t('commons.standards.last-name'),
        value: 'infos.lastName',
        width: 180,
      },
      {
        text: this.$t('commons.standards.first-name'),
        value: 'infos.firstName',
        width: 180,
      },
      {
        text: this.$t('commons.standards.birth-date'),
        value: 'infos.birthDate',
        width: 200,
      },
      {
        text: this.$t('commons.standards.foot-size'),
        value: 'infos.footSize',
        width: 140,
      },
      {
        text: this.$t('commons.standards.latest-acquisition'),
        value: '__latestAcquisition',
        sortable: false,
      },
      {
        text: this.$tc('commons.standards.action', 2),
        value: '__actions',
        sortable: false,
        width: 90,
        align: 'center',
      },
    ];

    // Set event listeners
    //this.$pdc.bus!.on('patient:added', this.search);
    //this.$pdc.bus!.on('patient:edited', this.search);
    //this.$pdc.bus!.on('patient:removed', this.search);
    this.$pdc.bus!.on(`entities:patients:created`, this.search);
    this.$pdc.bus!.on(`entities:patients:patched`, this.search);
    this.$pdc.bus!.on(`entities:patients:removed`, this.search);
    this.$pdc.bus!.on(`entities:acquisitions:patched`, this.search);
  },
  beforeDestroy() {
    // Destroy event listeners
    //this.$pdc.bus!.off('patient:added', this.search);
    //this.$pdc.bus!.off('patient:edited', this.search);
    //this.$pdc.bus!.off('patient:removed', this.search);
    this.$pdc.bus!.off(`entities:patients:created`, this.search);
    this.$pdc.bus!.off(`entities:patients:patched`, this.search);
    this.$pdc.bus!.off(`entities:patients:removed`, this.search);
    this.$pdc.bus!.off(`entities:acquisitions:patched`, this.search);
  },
})
export default class PatientsList extends Vue {
  $refs!: {
    delete: any;
  };

  table: TSyncableTable = {
    // Values prefixed with "__" are virtuals : they are not in entity documents, they are only used
    //    to customize the table with row that have access to the entire document.
    headers: [],
    patients: {
      list: [],
      total: 0,
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['infos.lastName'],
      sortDesc: [false],
    },
    search: {
      fields: ['infos.firstName', 'infos.lastName', 'infos.birthDate', 'infos.footSize'],
      value: '',
    },
    pending: false,
    delay: {
      config: 400,
      timeout: null,
    },
    error: null,
  };
  footerProps = {
    'items-per-page-options': [5, 10, 15, 50],
  };
  private previousKeyword: string | null = null;

  @Watch('table.options', { deep: true })
  private async onOptionsChange(newOptions: TSyncableTable['options'], oldOptions: TSyncableTable['options']) {
    // Set "loading" flag
    this.table.pending = true;
    // Clear previous timeout
    if (this.table.delay.timeout) clearTimeout(this.table.delay.timeout);

    await this.search({ optionsChanged: oldOptions });
  }

  @Watch('table.search.value')
  private async onSearchChange(newKeyword: any, oldKeyword: any) {
    // Set "loading" flag
    this.table.pending = true;
    // Clear previous timeout
    if (this.table.delay.timeout) clearTimeout(this.table.delay.timeout);

    if (newKeyword?.length) {
      this.table.delay.timeout = setTimeout(() => {
        return this.search();
      }, this.table.delay.config);
    } else {
      return this.search();
    }
  }

  async search(options?: { optionsChanged?: TSyncableTable['options'] }) {
    try {
      // Reset 'error' flag
      this.table.error = null;
      // Set 'pending' flag
      this.table.pending = true;

      // Set query
      const query: any = {
        ...this.$pdc.entities?.patients.defaultQuery,
        $limit: this.table.options.itemsPerPage === -1 ? this.table.patients.total : this.table.options.itemsPerPage,
        $skip: this.table.options.itemsPerPage * this.table.options.page - this.table.options.itemsPerPage,
        $sort: {
          [this.table.options.sortBy[0]]: this.table.options.sortDesc[0] ? -1 : 1,
        },
        $populate: {
          path: 'acquisitions',
          options: {
            sort: {
              createdAt: -1,
            },
            select: {
              createdAt: true,
            },
          },
        },
      };

      if (this.table.search.value?.length) {
        query['$or'] = this.table.search.fields.map((x) => {
          if (x === 'infos.footSize') {
            return {
              [x]: parseFloat(this.table.search.value) || 0,
            };
          }
          if (x === 'infos.birthDate') {
            return {
              [x]: '',
            };
          }
          return {
            [x]: {
              $regex: `(.*)${this.table.search.value}(.*)`,
              $options: 'i',
            },
          };
        });
      }

      // Try to retreive entities...
      const response = await this.$pdc.entities!.patients.search(this.table.search.value, {
        query,
        replace: true,
      });

      this.table.patients = {
        list: response.data,
        total: response.total,
      };

      // Analytics
      if (options?.optionsChanged) {
        const _d = {
          sortBy: this.table.options.sortBy[0],
          sortDesc: this.table.options.sortDesc[0],
        };
        const _p = {
          sortBy: options.optionsChanged.sortBy[0],
          sortDesc: options.optionsChanged.sortDesc[0],
        };

        if (JSON.stringify(_d) !== JSON.stringify(_p)) {
          this.$pdc.analytics?.actions.trackSuccess(
            'Patient ListSorted',
            {
              // keyword: this.table.search.value,
              // options: this.table.options
              ..._d,
            },
            {
              // options: options.optionsChanged
              ..._p,
            },
          );
        }
      } else if (this.previousKeyword !== this.table.search.value) {
        this.$pdc.analytics?.actions.trackSuccess(
          'Patient ListFiltered',
          {
            keyword: this.table.search.value,
            count: this.table.patients.list.length,
            // options: this.table.options
          },
          {
            keyword: this.previousKeyword,
          },
        );
      }
    } catch (e) {
      // Analytics
      if (options?.optionsChanged) {
        const _d = {
          sortBy: this.table.options.sortBy[0],
          sortDesc: this.table.options.sortDesc[0],
        };
        const _p = {
          sortBy: options.optionsChanged.sortBy[0],
          sortDesc: options.optionsChanged.sortDesc[0],
        };

        if (JSON.stringify(_d) !== JSON.stringify(_p)) {
          this.$pdc.analytics?.actions.trackFailure(
            'Patient ListSorted',
            e,
            {
              // keyword: this.table.search.value,
              // options: this.table.options
              ..._d,
            },
            {
              // options: options.optionsChanged
              ..._p,
            },
          );
        }
      } else if (this.previousKeyword !== this.table.search.value) {
        this.$pdc.analytics?.actions.trackFailure(
          'Patient ListFiltered',
          e,
          {
            keyword: this.table.search.value,
            // options: this.table.options
          },
          {
            keyword: this.previousKeyword,
          },
        );
      }

      // Set 'error' flag
      this.table.error = e;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    } finally {
      // Save keyword
      this.previousKeyword = this.table.search.value;

      // Reset 'pending' flag
      this.table.pending = false;
    }
  }

  isPatientAcquisitionInProgress(id: string) {
    if (this.$pdc.analysis!.manifestHasData) {
      return this.$pdc.analysis!.patientId === id;
    } else return false;
  }

  openPatient(patient: TEntity<TPatient>) {
    // Set 'pending' flag
    this.table.pending = true;

    this.$router.push({ path: `/patient/${patient._id}` }, () => {
      // Reset 'pending' flag
      this.table.pending = false;
    });
  }

  openDeleteDialog(patient: TEntity<TPatient>) {
    this.$refs.delete.open(patient);
  }
}
